import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../../services/api';
import { renderError } from '../../../helpers/errors';
import { SWATCH_PALETTE } from '../../../helpers/colors';
import { hasPermission } from '../../../services/auth';
import TagChip from '../../../components/TagChip';

const normalize = text => (text ?? '').trim().toLowerCase();

// Seção "Etiquetas" do card do CRM: chips sempre visíveis + popover para
// buscar, marcar/desmarcar e criar etiquetas inline. Cada toggle persiste na
// hora (update otimista com rollback em caso de erro).
const CardTagsField = ({ card, tags, onCardChange, onTagCreated }) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [creating, setCreating] = useState(false);
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    const cardTags = card.crm_tags ?? [];
    const selectedIds = cardTags.map(tag => tag.id);

    const filteredTags = tags.filter(tag => normalize(tag.name).includes(normalize(search)));
    const exactMatch = tags.some(tag => normalize(tag.name) === normalize(search));
    const canCreate = search.trim() !== '' && !exactMatch;

    useEffect(() => {
        if (!open) return;
        const handleClick = e => {
            if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
        };
        const handleKey = e => {
            if (e.key === 'Escape') setOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKey);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open]);

    useEffect(() => {
        if (open) {
            searchRef.current?.focus();
        } else {
            setSearch('');
        }
    }, [open]);

    const persist = (nextTags, revertTags) => {
        setSaving(true);
        onCardChange({ ...card, crm_tags: nextTags });
        return api.put(`/pipeline-stage-card/${card.id}`, {
            ...card,
            crm_tag_ids: nextTags.map(tag => tag.id),
        }).then(res => {
            onCardChange(res.data.resource);
            return true;
        }).catch(error => {
            renderError(error);
            onCardChange({ ...card, crm_tags: revertTags });
            return false;
        }).finally(() => setSaving(false));
    };

    const toggleTag = tag => {
        const isSelected = selectedIds.includes(tag.id);
        const next = isSelected
            ? cardTags.filter(t => t.id !== tag.id)
            : [...cardTags, tag];
        persist(next, cardTags);
    };

    const createTag = () => {
        const name = search.trim();
        if (name === '' || creating) return;
        setCreating(true);
        // Cor automática: próxima da paleta ainda não usada.
        const usedColors = new Set(tags.map(tag => tag.color));
        const color = SWATCH_PALETTE.find(c => !usedColors.has(c))
            ?? SWATCH_PALETTE[tags.length % SWATCH_PALETTE.length];
        api.post('/crm-tag', {
            name,
            color,
        }).then(res => {
            toast.success('Etiqueta criada');
            setSearch('');
            onTagCreated();
            persist([...cardTags, res.data.resource], cardTags);
        }).catch(renderError).finally(() => setCreating(false));
    };

    return (
        <div className="crm-tags-field" ref={containerRef}>
            <label>
                Etiquetas
                {saving && <span className="crm-tags-field__saving">Salvando...</span>}
            </label>
            <div className="crm-tags-field__chips">
                {cardTags.map(tag => (
                    <TagChip
                        key={tag.id}
                        name={tag.name}
                        color={tag.color}
                        onRemove={() => toggleTag(tag)}
                    />
                ))}
                {cardTags.length === 0 &&
                    <span className="crm-tags-field__empty">Nenhuma etiqueta</span>
                }
                <button
                    type="button"
                    className="crm-tags-field__add"
                    aria-label="Adicionar etiqueta"
                    aria-expanded={open}
                    onClick={() => setOpen(!open)}
                >
                    +
                </button>
            </div>
            {open &&
                <div className="crm-tags-field__popover">
                    <input
                        ref={searchRef}
                        type="text"
                        placeholder="Buscar ou criar etiqueta"
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter' && canCreate) createTag();
                        }}
                    />
                    <div className="crm-tags-field__options">
                        {filteredTags.map(tag => {
                            const selected = selectedIds.includes(tag.id);
                            return (
                                <button
                                    key={tag.id}
                                    type="button"
                                    className={`crm-tags-field__option${selected ? ' selected' : ''}`}
                                    onClick={() => toggleTag(tag)}
                                >
                                    <span className="crm-tags-field__check" aria-hidden="true">
                                        {selected ? '✓' : ''}
                                    </span>
                                    <TagChip name={tag.name} color={tag.color} />
                                </button>
                            );
                        })}
                        {filteredTags.length === 0 && !canCreate &&
                            <p className="crm-tags-field__none">Nenhuma etiqueta ainda. Digite um nome para criar.</p>
                        }
                    </div>
                    {(canCreate || hasPermission('edit_crm_settings')) &&
                        <div className="crm-tags-field__footer">
                            {canCreate &&
                                <button
                                    type="button"
                                    className="crm-tags-field__create"
                                    onClick={createTag}
                                    disabled={creating}
                                >
                                    {creating ? 'Criando...' : `+ Criar etiqueta "${search.trim()}"`}
                                </button>
                            }
                            {hasPermission('edit_crm_settings') &&
                                <Link to="/crm/etiquetas" className="crm-tags-field__manage">
                                    Gerenciar etiquetas
                                </Link>
                            }
                        </div>
                    }
                </div>
            }
        </div>
    );
};

export default CardTagsField;
